import {
    type InferFrom,
    type InferMaskFrom,
    type InferMaskTo,
    type InferTo,
    type RegisteredRouter,
    type NavigateOptions as TanstackNavigateOptions,
    useRouter,
} from '@tanstack/react-router';
import React from 'react';

import { buildHrefWith } from './links/buildHref';
import type { AppLinkOptions } from './links/linkOptions';
import { movedPagesOf } from './movedPages';
import { routeNameOf } from './routeName';
import type { NavigationState } from './useHistoryState';

/** What typed options add to TanStack's navigate options. */
type TypedNavigateExtras = {
    /** Opens `buildHref(options)` in a new tab instead of navigating, without `window.opener`. */
    openInNewTab?: boolean;
};

/**
 * TanStack's navigate options for the route `T` names, plus {@link TypedNavigateExtras}. Their `state` is typed by
 * the keys declared on `NavigationState`.
 */
// `NoInfer`, as in `buildHref`: `T` is inferred from the options themselves. Walking the constraint with a stored
// `AppLinkOptions` cost half a second per call when this overload came first and failed before the `linkOptions()`
// one matched.
type TypedNavigateConstraint<T> = NoInfer<
    TanstackNavigateOptions<RegisteredRouter, InferFrom<T, string>, InferTo<T>, InferMaskFrom<T>, InferMaskTo<T>> &
        TypedNavigateExtras
>;

/** The search object a route takes, without TanStack's `true` (keep the search) and updater forms. */
type DeclaredSearch<TConstraint> = Exclude<
    TConstraint extends { search?: infer TSearch } ? TSearch : never,
    true | undefined | ((...args: never) => unknown)
>;

/** The keys of a `search` object in `T` that the route doesn't declare. An updater or `true` has none. */
type UndeclaredSearchKeys<T, TConstraint> = T extends { search: infer TSearch extends object }
    ? TSearch extends (...args: never) => unknown
        ? never
        : Exclude<keyof TSearch, keyof DeclaredSearch<TConstraint>>
    : never;

/** The keys of a `state` object in `T` that `NavigationState` doesn't declare. An updater or `true` has none. */
type UndeclaredStateKeys<T> = T extends { state: infer TState extends object }
    ? TState extends (...args: never) => unknown
        ? never
        : Exclude<keyof TState, keyof NavigationState>
    : never;

/**
 * TanStack's `Constrain`, except that options with an undeclared search or state key are checked against the
 * constraint itself, which reports the key as an excess property, as on a link; `Constrain` alone accepts it next to
 * a declared key.
 */
type ValidateTypedNavigateOptions<T, TConstraint = TypedNavigateConstraint<T>> =
    | (T extends TConstraint
          ? [UndeclaredSearchKeys<T, TConstraint> | UndeclaredStateKeys<T>] extends [never]
              ? T
              : never
          : never)
    | TConstraint;

/** Options `linkOptions()` checked and branded where they were written. */
type LinkedNavigateOptions = AppLinkOptions & TypedNavigateExtras;

/** A URL that only exists at runtime. See {@link NavigateFunction}. */
type HrefNavigateOptions = {
    href: string;
    replace?: boolean;
    state?: NavigationState;
};

type RouterHistory = ReturnType<typeof useRouter>['history'];

const HREF_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/** The typed overload comes last: tsc reports the last one's error, which must be the route tree's. */
export interface NavigateFunction {
    /** Options from `linkOptions()`, a union of them, or a stored `AppLinkOptions`; spread them to add `replace`. */
    (options: LinkedNavigateOptions): void;
    /** Runtime URL: same-origin or relative (to the origin) pushes, http(s)/mailto/tel open a tab, the rest throw. */
    (options: HrefNavigateOptions): void;
    /** An app route, checked against the route tree like a link's `to`. */
    <const T>(options: ValidateTypedNavigateOptions<T>): void;
}

type AnyNavigateArgument = HrefNavigateOptions | ({ to?: unknown } & TypedNavigateExtras);

/**
 * Imperative navigation by typed options, `linkOptions()` results or `{ href }`; callable outside a router.
 * A page under its second route (`movedPages`) that has to leave it for its first navigates by `href`: `to` keeps it.
 */
// A click that only navigates is a link instead: `to` on the component (`routing/prefer-link` asks for it).
export function useNavigate(): NavigateFunction {
    // `null` outside a router despite the non-nullable declared return type.
    const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null;
    const history = router?.history;

    return React.useCallback(
        (to: AnyNavigateArgument) => {
            if (router === null || history === undefined) {
                throw new Error('useNavigate can only navigate when mounted under a router.');
            }
            // `href` wins over `to`.
            if ('href' in to) {
                navigateToHref(history, to);
                return;
            }
            const { openInNewTab, ...navigateOptions } = to;
            if (openInNewTab) {
                window.open(buildHrefWith(router, navigateOptions), '_blank', 'noopener');
                return;
            }
            // A page under its second route (`createAppRouter`'s `movedPages`) that navigates to its first stays on the
            // second. The app would redirect it there, and a redirect drops this navigation's `resetScroll`.
            const moved = movedPagesOf(router).find(({ from }) => from === navigateOptions.to);
            const stays = moved !== undefined && routeNameOf(router, router.state.matches) === moved.to;
            void router.navigate((stays ? { ...navigateOptions, to: moved.to } : navigateOptions) as never);
        },
        [router, history],
    ) as NavigateFunction;
}

function navigateToHref(history: RouterHistory, { href, replace, state }: HrefNavigateOptions): void {
    const url = new URL(href, window.location.origin);
    // Before the origin check: `javascript:` and `data:` URLs have the origin `'null'`.
    if (!HREF_PROTOCOLS.has(url.protocol)) {
        throw new Error('navigate({ href }) only opens http(s), mailto: and tel: URLs');
    }
    if (url.origin !== window.location.origin) {
        window.open(url.href, '_blank', 'noopener');
        return;
    }
    const path = url.pathname + url.search + url.hash;
    if (replace) {
        history.replace(path, state);
    } else {
        history.push(path, state);
    }
}
